import React from "react";
import { useAuth } from "../../context/API_Client";
import HoverToDisplayFullTitle from "../../reusableComponents/HoverToDisplayFullTitle";
import { Skeleton, Tooltip, Typography, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { isEmpty, sortDateTime, convertIsoToGivenDateFormat, convertIsoToGivenMonthYear, isSameDate } from "../utils/utils";
export function GenerateReportSchema(
  handleDownload,
  operationPermissions,
  fileSetConfig,
  timezone
) {
  const { isAuthorizedPermission } = useAuth();
  const schema = {
    metadata: {
      title: "All Reports",
      minHeight: 600,
      position: "relative",
      rowHeight: 60,
      showTitle: false,
      defaultSortInfo: { name: "timestamp", dir: -1 },
      defaultVisible: [
        "fileName",
        "timestamp",
        "reportType",
        "insurer",
        "timestamp",
        "timeline",
        "status",
      ],
    },
    fields: [
      {
        name: "fileName",
        header: "File Name",
        visible: true,
        type: "string",
        filtering: true,
        minWidth: 180,
        defaultFlex: 1,
        textAlign: "start",
        sortable: false,
        render: ({ data, ...rest }) => (
          <div
            style={{
              marginLeft: "-12px",
              maxWidth: ` ${rest?.cellProps?.computedWidth ? `${rest?.cellProps?.computedWidth}px` : " 180px"}`,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >

            {data?.name && (
              <HoverToDisplayFullTitle title={data?.name} len={20} />
            )}
          </div>
        ),
      },
      {
        name: "timestamp",
        header: "Generated At",
        visible: true,
        type: "string",
        filtering: false,
        textAlign: "start",
        sortable: true,
        sort: sortDateTime,
        minWidth: 220,
        defaultFlex: 1,
        render: ({ data }) => (
          <span style={{ marginLeft: "-12px" }}>{convertIsoToGivenDateFormat(data.timestamp, timezone)}</span>
        ),
      },
      {
        name: "reportType",
        header: "Report Type",
        visible: true,
        type: "number",
        filtering: false,
        minWidth: 250,
        defaultFlex: 1,
        textAlign: "start",
        sortable: false,
        render: ({ data, ...rest }) => (
          <>
            <div
              style={{
                marginLeft: "-12px",
                maxWidth: ` ${rest?.cellProps?.computedWidth ? `${rest?.cellProps?.computedWidth}px` : " 250px"}`,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {data?.reconStatuses && (
                <HoverToDisplayFullTitle
                  title={isEmpty(data?.reconStatuses) ? 'All' : data?.reconStatuses?.toString()}
                  len={20}
                />
              )}
            </div>
          </>
        ),
      },
      {
        name: "insurer",
        header: "Insurer",
        visible: true,
        type: "string",
        filtering: false,
        minWidth: 250,
        defaultFlex: 1,
        textAlign: "start",
        sortable: false,
        render: ({ data, ...rest }) => {

          // commented for future use 
          // let matchedValues = [];
          // if (data?.fileSetCodes && data?.fileSetCodes?.length) {
          //   matchedValues = fileSetConfig
          //     .filter((obj) => data.fileSetCodes.includes(obj.fileSetCode))
          //     .map((obj) => obj.fileSetTitle);
          // }
          // const titles = matchedValues?.length ? matchedValues.join(" , ") : "";
          let titles = data?.tags?.length ? data?.tags?.join(" , ") : ""
          return (
            <>
              <div
                style={{
                  marginLeft: "-12px",
                  maxWidth: ` ${rest?.cellProps?.computedWidth ? `${rest?.cellProps?.computedWidth}px` : " 250px"}`,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {titles && <HoverToDisplayFullTitle title={titles} len={20} />}
              </div>
            </>
          );
        },
      },
      {
        name: "timeline",
        header: "Timeline",
        visible: true,
        type: "string",
        filtering: false,
        minWidth: 220,
        defaultFlex: 1,
        textAlign: "center",
        sortable: false,
        render: ({ data }) => {

          const isTimeStampSame = isSameDate(data?.fromTimestamp, data?.toTimestamp)
          return <span style={{ display: "flex", marginLeft: "-12px" }}>
            {isTimeStampSame ? convertIsoToGivenMonthYear(data?.fromTimestamp, timezone) : <> {convertIsoToGivenMonthYear(data?.fromTimestamp, timezone)} - {convertIsoToGivenMonthYear(data?.toTimestamp, timezone)} </>}
          </span>
        },
      },
      {
        name: "status",
        header: "Status",
        visible: true,
        type: "number",
        filtering: false,
        minWidth: 150,
        defaultFlex: 1,
        textAlign: "start",
        sortable: false,
        render: ({ value, data }) => {
          let backgroundColor;
          let color;
          const status = data?.status;
          const url = data?.downloadLink;
          if (value === "Complete") {
            backgroundColor = "#D5FBD2";
            color = "#0B6A48";
          } else if (value === "Pending") {
            backgroundColor = "#FBEFCB";
            color = "#9E5707";
          } else if (value === "Failed") {
            backgroundColor = "var(--comms-negative-sub, #FDE3D6)";
            color = "var(--comms-negative, #B9232F)";
          } else if (value === "Empty") {
            backgroundColor = "var(--Neutral-4, #F0F0F0)";
            color = "var(--Neutral-9, #434343)";
          }
          return (
            <div
              style={{
                marginLeft: "-20px",
                paddingBottom: "6px",
                paddingRight: "8px",
                paddingLeft: "8px",
                paddingTop: "5px",
                borderRadius: "8px",
                backgroundColor,
                color,
                textTransform: "uppercase",
                lineHeight: "1.5",
                display: "flex",
                alignItems: "center",
              }}
            >
              <span>{value}</span>
              <span className="report-tooltip">
                {isAuthorizedPermission(
                  operationPermissions?.downloadGenReconReport
                ) &&
                  data?.status?.toLowerCase() == "complete" &&
                  !url && (
                    <Tooltip
                      title="No records available"
                      sx={{ textAlign: "center", fontFamily: "poppins" }}
                    >
                      <IconButton
                        sx={{
                          "&:hover": { backgroundColor: "transparent" },
                        }}
                        disableFocusRipple
                        disableTouchRipple
                      >
                        <InfoOutlinedIcon
                          sx={{
                            color: "#0B6A48",
                            width: "16px",
                            height: "16px",
                            order: 1,
                            marginLeft: "2px",
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
              </span>
            </div>
          );
        },
      },
      {
        name: "download",
        header: "Download",
        visible: isAuthorizedPermission(
          operationPermissions?.downloadGenReconReport
        ),
        type: "string",
        filtering: false,
        minWidth: 150,
        defaultFlex: 1,
        textAlign: "center",
        sortable: false,
        render: ({ data }) => {
          const status = data?.status;
          const url = data?.downloadLink;

          return (
            <span style={{ display: "flex", marginLeft: "-12px" }}>
              {isAuthorizedPermission(
                operationPermissions?.downloadGenReconReport
              ) &&
                status === "Complete" &&
                url ? (
                <div>
                  <img
                    src="/assets/downloadReport.svg"
                    style={{
                      height: "28px",
                      width: "28px",
                      objectFit: "contain",
                      marginRight: "8px",
                      mixBlendMode: "multiply",
                      marginLeft: "20px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleDownload(url)}
                  />
                </div>
              ) : null}
            </span>
          );
        },
      },
    ],
  };

  return schema;
}
